// GYM-900 / GYM-479b / GYM-1048: Replay offline write queue against the backend.
//
// Generic dispatcher: reads url/method/body from the envelope and selects the
// correct auth token via tokenAudience. Preserves the 2xx|409=success,
// 4xx=drop, 5xx=retry, MAX_ATTEMPTS=3 contract from the original trainer queue.

import { dequeue, incrementAttempts, listQueue } from "./writeQueue";
import type { QueueEntry } from "./writeQueue";

const MAX_ATTEMPTS = 3;

async function replayEntry(
  entry: QueueEntry,
  getToken: (audience: "staff" | "member") => string | null,
): Promise<void> {
  const { action } = entry;
  const token = getToken(action.tokenAudience);
  if (!token) return; // not authenticated yet — skip this cycle

  const resp = await fetch(action.url, {
    method: action.method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(action.body),
  });

  // 2xx or 409 (already applied) — consider done.
  if (resp.ok || resp.status === 409) {
    await dequeue(entry.id!);
    return;
  }

  // 4xx client errors — no point retrying.
  if (resp.status >= 400 && resp.status < 500) {
    await dequeue(entry.id!);
    return;
  }

  // 5xx — transient; increment and try next cycle.
  throw new Error(`Server error ${resp.status}`);
}

export async function flushQueue(
  getToken: (audience: "staff" | "member") => string | null,
): Promise<void> {
  const entries = await listQueue();
  for (const entry of entries) {
    if (entry.attempts >= MAX_ATTEMPTS) {
      await dequeue(entry.id!);
      continue;
    }
    try {
      await replayEntry(entry, getToken);
    } catch {
      await incrementAttempts(entry.id!);
    }
  }
}
